import { yupResolver } from '@hookform/resolvers/yup'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import {
  FestivalGeneralSettings,
  FestivalModulesManager,
  FestivalOtherModules,
} from '../../../common/models'
import { MessageBox } from '../messages/MessageBox'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { formatDateTime } from '@/core/constants/times'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { Save, XCircle } from 'lucide-react'
import FormGroup from '@/core/components/FormGroup'
import { Icon } from '@/common/components/Icon'
import { ModuleManagement } from '../settings/settings-components/ModuleManagement'

interface IFestivalGeneralSettings {
  name?: string
  location?: boolean
  locationName?: string
  localeTime?: Date
  latitude?: string
  longitude?: string
  festivalCounter?: boolean
  logoPNG?: string
  logoSVG?: string
}

interface IModules {
  getTickets?: boolean
  artists?: boolean
  nrArtistsDisplayed?: number
  newsletter?: boolean
  news?: boolean
  nrNewsDisplayed?: number
  stages?: boolean
  appPromo?: boolean
  appSimulator?: string
  promoApps?: boolean
  videoGallery?: boolean
  instagramWidget?: boolean
  nrPostsDisplayed?: number
  ourFriendsWidget?: boolean
  footerCopyright?: string
}

interface IOtherModules {
  topUp?: boolean
  redeemTickets?: boolean
  redeemTopup?: boolean
  checkIn?: boolean
  memories?: boolean
}

export const FestivalSettingsPage: React.FC = () => {
  const [isUploadingGeneralSettings, setIsUploadingGeneralSettings] =
    useState<boolean>(false)
  const [isUploadingModules, setIsUploadingModules] = useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [generalSettings, setGeneralSettings] =
    useState<FestivalGeneralSettings>({
      id: '',
      name: '',
      location: false,
      locationName: '',
      localeTime: new Date(),
      latitude: '',
      longitude: '',
      festivalCounter: false,
      logoPNG: '',
      logoSVG: '',
    })
  const [modules, setModules] = useState<FestivalModulesManager>({
    getTickets: false,
    artists: false,
    nrArtistsDisplayed: 0,
    newsletter: false,
    news: false,
    nrNewsDisplayed: 0,
    stages: false,
    appPromo: false,
    appSimulator: '',
    promoApps: false,
    videoGallery: false,
    instagramWidget: false,
    nrPostsDisplayed: 0,
    ourFriendsWidget: false,
    footerCopyright: '',
  })
  const [otherModules, setOtherModules] = useState<FestivalOtherModules>({
    topUp: false,
    redeemTickets: false,
    redeemTopup: false,
    checkIn: false,
    memories: false,
  })

  const navigate = useNavigate()
  const { editionId } = useParams()

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        // const response = await http.get<IUserProfile>('/web/users/profile') make api call to get data
        const data: FestivalGeneralSettings = {
          id: '',
          name: '',
          location: false,
          locationName: '',
          localeTime: new Date(),
          latitude: '',
          longitude: '',
          festivalCounter: false,
          logoPNG: '',
          logoSVG: '',
        }
        setGeneralSettings(data)
        setValueGeneralSettings('name', generalSettings.name)
        setValueGeneralSettings('location', generalSettings.location)
        setValueGeneralSettings('locationName', generalSettings.locationName)
        setValueGeneralSettings('localeTime', generalSettings.localeTime)
        setValueGeneralSettings('latitude', generalSettings.latitude)
        setValueGeneralSettings('longitude', generalSettings.longitude)
        setValueGeneralSettings('festivalCounter', generalSettings.festivalCounter)
        setValueGeneralSettings('logoPNG', generalSettings.logoPNG)
        setValueGeneralSettings('logoSVG', generalSettings.logoSVG)

        const data2: FestivalModulesManager = {
          getTickets: false,
          artists: false,
          nrArtistsDisplayed: 0,
          newsletter: false,
          news: false,
          nrNewsDisplayed: 0,
          stages: false,
          appPromo: false,
          appSimulator: '',
          promoApps: false,
          videoGallery: false,
          instagramWidget: false,
          nrPostsDisplayed: 0,
          ourFriendsWidget: false,
          footerCopyright: ''
        }
        setModules(data2)
        setValueModules('getTickets', modules.getTickets)
        setValueModules('artists', modules.artists)
        setValueModules('nrArtistsDisplayed', modules.nrArtistsDisplayed)
        setValueModules('newsletter', modules.newsletter)
        setValueModules('news', modules.news)
        setValueModules('nrNewsDisplayed', modules.nrNewsDisplayed)
        setValueModules('stages', modules.stages)
        setValueModules('appPromo', modules.appPromo)
        setValueModules('appSimulator', modules.appSimulator)
        setValueModules('promoApps', modules.promoApps)
        setValueModules('videoGallery', modules.videoGallery)
        setValueModules('instagramWidget', modules.instagramWidget)
        setValueModules('nrPostsDisplayed', modules.nrPostsDisplayed)
        setValueModules('ourFriendsWidget', modules.ourFriendsWidget)
        setValueModules('footerCopyright', modules.footerCopyright)

        const data3: FestivalOtherModules = {
          topUp: false,
          redeemTickets: false,
          redeemTopup: false,
          checkIn: false,
          memories: false,
        }

        setOtherModules(data3)
        setValueOtherModules('checkIn', otherModules.checkIn)
        setValueOtherModules('memories', otherModules.memories)
        setValueOtherModules('redeemTickets', otherModules.redeemTickets)
        setValueOtherModules('redeemTopup', otherModules.redeemTopup)
        setValueOtherModules('topUp', otherModules.topUp)
      } catch (e) {
        console.error(e)
      }
    })()
  }, [])

  const schemaModules = yup.object().shape({
    getTickets: yup.boolean(),
    artists: yup.boolean(),
    nrArtistsDisplayed: yup.number(),
    newsletter: yup.boolean(),
    news: yup.boolean(),
    nrNewsDisplayed: yup.number(),
    stages: yup.boolean(),
    appPromo: yup.boolean(),
    appSimulator: yup.string(),
    promoApps: yup.boolean(),
    videoGallery: yup.boolean(),
    instagramWidget: yup.boolean(),
    nrPostsDisplayed: yup.number(),
    ourFriendsWidget: yup.boolean(),
    footerCopyright: yup.string(),
  })

  const {
    handleSubmit: handleSubmitModules,
    register: registerModules,
    formState: { errors: errorsModules },
    setValue: setValueModules,
    watch: watchModules,
  } = useForm<IModules>({
    mode: 'onChange',
    resolver: yupResolver<IModules>(schemaModules),
    defaultValues: {
      getTickets: false,
      artists: false,
      nrArtistsDisplayed: 0,
      newsletter: false,
      news: false,
      nrNewsDisplayed: 0,
      stages: false,
      appPromo: false,
      appSimulator: '',
      promoApps: false,
      videoGallery: false,
      instagramWidget: false,
      nrPostsDisplayed: 0,
      ourFriendsWidget: false,
      footerCopyright: '',
    },
  })

  const submitModules = async (values: IModules) => {
    console.log(values) //TODO replace with the submit api call
  }

  const handleCancelModules = async () => {
    setValueGeneralSettings('name', generalSettings.name)
    setValueGeneralSettings('location', generalSettings.location)
    setValueGeneralSettings('locationName', generalSettings.locationName)
    setValueGeneralSettings('localeTime', generalSettings.localeTime)
    setValueGeneralSettings('latitude', generalSettings.latitude)
    setValueGeneralSettings('longitude', generalSettings.longitude)
    setValueGeneralSettings('festivalCounter', generalSettings.festivalCounter)
    setValueGeneralSettings('logoPNG', generalSettings.logoPNG)
    setValueGeneralSettings('logoSVG', generalSettings.logoSVG)
  }

  const uploadImageModules = async (file: File) => {
    setIsUploadingModules(true)
    try {
      console.log(file)
      // const { data } = await uploadAdminsPhoto(editionId, file, "ImageCOnfig")
      // return data.photoUrl
    } catch (err) {
      console.log(err)
      return ''
    } finally {
      setIsUploadingModules(false)
    }
  }

  const schemaGeneralSettings = yup.object().shape({
    name: yup.string(),
    location: yup.boolean(),
    locationName: yup.string(),
    localeTime: yup.date(),
    latitude: yup.string(),
    longitude: yup.string(),
    festivalCounter: yup.boolean(),
    logoPNG: yup.string(),
    logoSVG: yup.string(),
  })

  const {
    handleSubmit: handleSubmitGeneralSettings,
    register: registerGeneralSettings,
    formState: { errors: errorsGeneralSettings },
    setValue: setValueGeneralSettings,
    watch: watchGeneralSettings,
    control: controlGeneralSettings,
  } = useForm<IFestivalGeneralSettings>({
    mode: 'onChange',
    resolver: yupResolver<IFestivalGeneralSettings>(schemaGeneralSettings),
    defaultValues: {
      name: '',
      location: false,
      locationName: '',
      localeTime: new Date(),
      latitude: '',
      longitude: '',
      festivalCounter: false,
      logoPNG: '',
      logoSVG: '',
    },
  })

  const submitGeneralSettings = async (values: IFestivalGeneralSettings) => {
    console.log(values) //TODO replace with the submit api call
  }

  const handleCancelGeneralSettings = async () => {
    setValueGeneralSettings('name', generalSettings.name)
    setValueGeneralSettings('location', generalSettings.location)
    setValueGeneralSettings('locationName', generalSettings.locationName)
    setValueGeneralSettings('localeTime', generalSettings.localeTime)
    setValueGeneralSettings('latitude', generalSettings.latitude)
    setValueGeneralSettings('longitude', generalSettings.longitude)
    setValueGeneralSettings('festivalCounter', generalSettings.festivalCounter)
    setValueGeneralSettings('logoPNG', generalSettings.logoPNG)
    setValueGeneralSettings('logoSVG', generalSettings.logoSVG)
  }

  const uploadImageGeneralSettings = async (file: File) => {
    setIsUploadingGeneralSettings(true)
    try {
      console.log(file)
      // const { data } = await uploadAdminsPhoto(editionId, file)
      // return data.photoUrl
    } catch (err) {
      console.log(err)
      return ''
    } finally {
      setIsUploadingGeneralSettings(false)
    }
  }

  const schemaOtherModules = yup.object().shape({
    topUp: yup.boolean(),
    redeemTickets: yup.boolean(),
    redeemTopup: yup.boolean(),
    checkIn: yup.boolean(),
    memories: yup.boolean(),
  })

  const {
    handleSubmit: handleSubmitOtherModules,
    register: registerOtherModules,
    formState: { errors: errorsOtherModules },
    setValue: setValueOtherModules,
    watch: watchOtherModules,
    control: controlOtherModules,
  } = useForm<IOtherModules>({
    mode: 'onChange',
    resolver: yupResolver<IOtherModules>(schemaOtherModules),
    defaultValues: {
      topUp: false,
      redeemTickets: false,
      redeemTopup: false,
      checkIn: false,
      memories: false,
    },
  })

  const submitOtherModules = async (values: IOtherModules) => {
    console.log(values) //TODO replace with the submit api call
  }

  const handleCancelOtherModules = async () => {
    setValueOtherModules('checkIn', otherModules.checkIn)
    setValueOtherModules('memories', otherModules.memories)
    setValueOtherModules('redeemTickets', otherModules.redeemTickets)
    setValueOtherModules('redeemTopup', otherModules.redeemTopup)
    setValueOtherModules('topUp', otherModules.topUp)
  }

  return (
    <>
      <Header
        pageTitle="Festival Settings"
        breadcrumbs={['Global Settings', 'Events', `${editionId}`]}
      />
      <div className="flex flex-row items-center justify-between gap-3 mb-5 w-full">
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <Icon
            icon="arrow-left"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          />
          Back
        </Button>
        <span className="text-title-s text-monochrome-1">{editionId}</span>
        <div className="min-w-[82px]" />
      </div>
      <div className="max-h-[calc(100vh-185px)] overflow-y-auto">
        {/* <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                handleCancelGeneralSettings()
                handleCancelModules()
                handleCancelOtherModules()
              }}
            >
              <XCircle className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                handleSubmitGeneralSettings(submitGeneralSettings)
                handleSubmitModules(submitModules)
                handleSubmitOtherModules(submitOtherModules)
              }}
            >
              <Save className="w-4 h-4" />
              Save
            </Button>
          </div>
        </div> */}
        <div className="flex flex-col gap-5">
          <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-title-xs text-monochrome-1">
                General Settings
              </h4>
            </div>

            <div className="space-y-4">
              <form
                id="settings-general-settings-form"
                onSubmit={handleSubmitGeneralSettings(submitGeneralSettings)}
              >
                <div className="flex flex-col items-start gap-3">
                  <FormGroup label="Festival Name">
                    <Input
                      isInvalid={!!errorsGeneralSettings?.name?.message}
                      {...registerGeneralSettings('name')}
                      type="text"
                      placeholder="Enter Name"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Location"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'location',
                    }}
                  >
                    <Controller
                      control={controlGeneralSettings}
                      name="location"
                      render={({ field }) => (
                        <Checkbox
                          id="location"
                          ref={field.ref}
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(Boolean(checked))
                          }}
                        />
                      )}
                    />
                  </FormGroup>
                  <FormGroup label="Location Name">
                    <Input
                      isInvalid={!!errorsGeneralSettings?.locationName?.message}
                      {...registerGeneralSettings('locationName')}
                      type="text"
                      placeholder="Enter Location Name"
                    />
                  </FormGroup>
                  <FormGroup label="Local Time">
                    <Controller
                      control={controlGeneralSettings}
                      name="localeTime"
                      render={({ field }) => (
                        <DateTimePicker
                          value={field.value}
                          className="h-[44px] rounded-2xl"
                          onChange={field.onChange}
                          placeholder="Enter Locale Time"
                          hasError={
                            !!errorsGeneralSettings?.localeTime?.message
                          }
                        />
                      )}
                    />
                  </FormGroup>
                  <FormGroup label="Latitude">
                    <Input
                      isInvalid={!!errorsGeneralSettings?.latitude?.message}
                      {...registerGeneralSettings('latitude')}
                      type="text"
                      placeholder="Enter Copyright message"
                    />
                  </FormGroup>
                  <FormGroup label="Longitude">
                    <Input
                      isInvalid={!!errorsGeneralSettings?.longitude?.message}
                      {...registerGeneralSettings('longitude')}
                      type="text"
                      placeholder="Enter Longitude"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Festival Counter"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'festivalCounter',
                    }}
                  >
                    <Controller
                      control={controlGeneralSettings}
                      name="festivalCounter"
                      render={({ field }) => (
                        <Checkbox
                          id="festivalCounter"
                          ref={field.ref}
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(Boolean(checked))
                          }}
                        />
                      )}
                    />
                  </FormGroup>
                  <FormGroup label="Logo Upload PNG (145X40)">
                    <Input
                      isInvalid={!!errorsGeneralSettings?.logoPNG?.message}
                      {...registerGeneralSettings('logoPNG')}
                      type="file"
                      accept="image/*"
                    />
                  </FormGroup>
                  <FormGroup label="Logo Upload SVG">
                    <Input
                      isInvalid={!!errorsGeneralSettings?.logoSVG?.message}
                      {...registerGeneralSettings('logoSVG')}
                      type="file"
                      accept="image/*"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            setInvalidPhoto(true)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The logo is too large (1 MB)')
                            return
                          }
                          const url = await uploadImageGeneralSettings(file)
                          console.log(url)

                          if (url) {
                            setValueGeneralSettings('logoSVG', url as string, {
                              shouldValidate: true,
                            })
                          }
                        }
                      }}
                    />
                  </FormGroup>
                </div>
              </form>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
              <Button
                onClick={handleCancelGeneralSettings}
                variant="secondary"
                data-dismiss="modal"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                form="settings-general-settings-form"
                type="submit"
                disabled={isUploadingGeneralSettings}
              >
                Save
              </Button>
            </div>
          </Card>
          <ModuleManagement hasEditPermission={true} />
          <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
            <form
              id="settings-other-modules-form"
              onSubmit={handleSubmitOtherModules(submitOtherModules)}
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-title-xs text-monochrome-1">
                  Other Modules
                </h4>
              </div>
              <div className="grid grid-cols-5 gap-4 py-4 divide-x divide-monochrome-120">
                <FormGroup
                  label="Top Up"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                  inputWrapperClassName="w-auto"
                  wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                  labelProps={{
                    htmlFor: `topUp`,
                  }}
                >
                  <Controller
                    control={controlOtherModules}
                    name="topUp"
                    render={({ field }) => (
                      <Checkbox
                        id="topUp"
                        ref={field.ref}
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(Boolean(checked))
                        }}
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup
                  label="Reedeem Tickets"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                  inputWrapperClassName="w-auto"
                  wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                  labelProps={{
                    htmlFor: `redeemTickets`,
                  }}
                >
                  <Controller
                    control={controlOtherModules}
                    name="redeemTickets"
                    render={({ field }) => (
                      <Checkbox
                        id="redeemTickets"
                        ref={field.ref}
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(Boolean(checked))
                        }}
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup
                  label="Reedeem Topup"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                  inputWrapperClassName="w-auto"
                  wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                  labelProps={{
                    htmlFor: `redeemTopup`,
                  }}
                >
                  <Controller
                    control={controlOtherModules}
                    name="redeemTopup"
                    render={({ field }) => (
                      <Checkbox
                        id="redeemTopup"
                        ref={field.ref}
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(Boolean(checked))
                        }}
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup
                  label="Check-in"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                  inputWrapperClassName="w-auto"
                  wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                  labelProps={{
                    htmlFor: `checkIn`,
                  }}
                >
                  <Controller
                    control={controlOtherModules}
                    name="checkIn"
                    render={({ field }) => (
                      <Checkbox
                        id="checkIn"
                        ref={field.ref}
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(Boolean(checked))
                        }}
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup
                  label="Memories"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                  inputWrapperClassName="w-auto"
                  wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                  labelProps={{
                    htmlFor: `memories`,
                  }}
                >
                  <Controller
                    control={controlOtherModules}
                    name="memories"
                    render={({ field }) => (
                      <Checkbox
                        id="memories"
                        ref={field.ref}
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(Boolean(checked))
                        }}
                      />
                    )}
                  />
                </FormGroup>
              </div>
            </form>

            <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
              <Button onClick={handleCancelOtherModules} variant="secondary">
                Cancel
              </Button>
              <Button
                variant="primary"
                form="settings-other-modules-form"
                type="submit"
              >
                Save
              </Button>
            </div>
          </Card>
        </div>
      </div>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
